import {
  useGetSessionDataQuery,
  useTubeEditLinkQuery,
  useGetProductInformationQuery,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import { TubeProperties, FileFormat } from './enum'
import { InvalidKeys } from '@src/routes/Configurators/utils/constants'
import { Message } from '../messages'
import { FormattedMessage } from 'react-intl'

export const FetchTubeEditLinkQuery = async (productDetails) => {
  const { data } = useTubeEditLinkQuery({
    variables: {
      product: productDetails,
    },
    fetchPolicy: 'no-cache',
  })
  if (data) {
    return data.tubeEditLink?.data
  }

  return null
}
export const FetchSessionDataQuery = async (productDetails) => {
  const { data } = useGetSessionDataQuery({
    variables: {
      product: productDetails,
    },
    fetchPolicy: 'no-cache',
  })
  if (data) {
    return data.getSessionData?.data
  }

  return null
}

export const FetchProductInformation = async (productDetails) => {
  const { data } = useGetProductInformationQuery({
    variables: {
      input: productDetails,
    },
    fetchPolicy: 'no-cache',
  })
  if (data && data.getProductInformation) {
    return data.getProductInformation
  }

  return null
}

export const getProductStatus = (data: string) => {
  if (data === TubeProperties.UnAuthorized) {
    return 1
  } else if (data.includes(TubeProperties.InvalidProduct)) {
    return 2
  } else if (data.includes(TubeProperties.ProductNotValid)) {
    return 3
  } else if (data.includes(TubeProperties.UserCannotOrder)) {
    return 4
  } else {
    return 0
  }
}

export const validateText = (data: string) => {
  return data
    .replace(/[\u007F-\u00A0]/g, '')
    .replace(/\\f/g, '')
    .replace(/%/g, '')
}

//method for converting multibyte character to ascii
export const convertToASCII = (charSequence) => {
  let ascii = ''
  for (let i = 0, l = charSequence.length; i < l; i++) {
    let char = charSequence.charCodeAt(i)

    // make sure we only convert half-full width char
    if (char >= 0xff00 && char <= 0xffef) {
      char = 0xff & (char + 0x20)
    }
    ascii += String.fromCharCode(char)
  }
  return ascii
}

// This method will sort the array of object
export const SortData = (property) => {
  let sortOrder = 1
  if (property[0] === '-') {
    sortOrder = -1
    property = property.substr(1)
  }
  return function (a, b) {
    /* next line works with strings and numbers,
     * and you may want to customize it to your needs
     */
    const result =
      a[property].toLowerCase() < b[property].toLowerCase()
        ? -1
        : a[property].toLowerCase() > b[property].toLowerCase()
          ? 1
          : 0
    return result * sortOrder
  }
}

export const isIEBrowser = () => {
  return !!window.document['documentMode']
}
export const getMaxId = (data) => {
  let maxId = data[data.length - 1].oligoId
  data.forEach((item) => {
    if (item.oligoId > maxId) {
      maxId = item.oligoId
    }
  })
  return maxId
}
/*GA data for Configurator Products
  common method to use it in summary.tsx files  for GA
 */

export const getConfiguratorGAdata = (
  obj,
  sessionData,
  itemName,
  qty,
  locale,
  currencyFormat
) => {
  let productPrice = 0
  const averagePrice = () => {
    sessionData.forEach((product) => {
      if (product.price && product.price !== TubeProperties.Quote) {
        productPrice = productPrice + Number(product.price)
      } else {
        productPrice = productPrice + 1
      }
    })
    return (productPrice / qty).toFixed(Number(currencyFormat))
  }
  const avgPrice = averagePrice()
  let gaPrice
  let gaFormattedPrice
  try {
    gaPrice = avgPrice.slice(0, avgPrice.length - 3).replace(/[.,]/g, '')
    gaFormattedPrice =
      gaPrice + avgPrice.slice(avgPrice.length - 3, avgPrice.length)
  } catch {
    gaFormattedPrice = avgPrice
  }
  return {
    productDetails: obj,
    totalQuantity: qty,
    price: `${gaFormattedPrice}`,
    productName: itemName,
  }
}

export const validateSurrogateCharacters = (comments) => {
  let surrogateChar = ''
  for (const char of comments) {
    if (char.length > 1 && !surrogateChar.includes(char)) {
      if (surrogateChar === '') {
        surrogateChar = char
      } else {
        surrogateChar = surrogateChar + ', ' + char
      }
    }
  }
  return surrogateChar
}

export const checkValidKey = (
  msg: Message,
  key: string = InvalidKeys.KEY_NOT_PRESENT
): Message => {
  if (typeof msg === 'object' && typeof msg.id !== 'undefined') return msg

  // do error reporting here.
  // if wanted return a fallback message
  return {
    id: key,
    defaultMessage: key,
  }
}

interface RenderFormattedMessageProps {
  displayName: string
  messages: { [key: string]: { id: string; defaultMessage: string } }
  fallback?: string
}

export const RenderFormattedMessage: React.FC<RenderFormattedMessageProps> = ({
  displayName,
  messages,
  fallback,
}) => {
  const message = messages[displayName]
  return message ? (
    <FormattedMessage {...message} />
  ) : (
    <span>{fallback || displayName}</span>
  )
}

export const checkMultipleLine = (products, resolve) => {
  let isMultipleLineError = false
  products.forEach((data) => {
    if (!isMultipleLineError) {
      isMultipleLineError = data.name.includes('\n')
    }
  })
  if (isMultipleLineError) {
    resolve(FileFormat.MultipleLineError)
  }
}
export const checkMultipleLineWithcolumn = (products, resolve, column) => {
  let isMultipleLineError = false
  products.forEach((data) => {
    if (!isMultipleLineError) {
      isMultipleLineError = data[column].includes('\n')
    }
  })
  if (isMultipleLineError) {
    resolve(FileFormat.MultipleLineError)
  }
}

export const removeKeys = (value, keysToRemove) => {
  if (Array.isArray(value)) {
    return value.map((item) => removeKeys(item, keysToRemove))
  } else if (typeof value === 'object' && value !== null) {
    const newObj = {}
    for (const key in value) {
      if (keysToRemove.includes(key) && key !== 'type') {
        continue
      }
      newObj[key] = removeKeys(value[key], keysToRemove)
    }
    return newObj
  } else {
    return value
  }
}

export const validateTextUpdated = (data: string) => {
  return data.replace(/[\u007F-\u00A0]/g, '').replace(/\\f/g, '')
}

export const queryParamString = (
  param: string | string[] | undefined
): string => {
  return Array.isArray(param) ? param[0] : param || ''
}

export const getDefaultOrConvertToArray = (
  param: string | string[] | undefined | null
): string[] => {
  let array: string[] = []
  if (Array.isArray(param)) {
    array = [...param]
  } else {
    array.push(param || '')
  }
  return array
}
